import { useEffect, useState } from 'react';
import { recordsApi, type VoicemailEmailChoices } from '../api/client';

/**
 * Voicemail emails for your own mailbox, in Your settings (inside a company where you have your own
 * mailbox and the company has voicemail emails on): whether they're sent, and with the audio
 * attached. They go to the email address on your extension, and the choice is the mailbox's, shared
 * by everyone signed in as it.
 */
export function useVoicemailEmailChoices(domain: string | undefined) {
  const [view, setView] = useState<VoicemailEmailChoices | null>(null);
  const [choice, setChoice] = useState<{ send: boolean; attach: boolean } | null>(null);
  useEffect(() => {
    setView(null);
    setChoice(null);
    if (!domain) return;
    let gone = false;
    recordsApi
      .voicemailEmails(domain)
      .then((v) => {
        if (gone) return;
        setView(v);
        setChoice(v.effective);
      })
      .catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [domain]);
  const shown = Boolean(view?.available && view.on && choice);
  const changed = Boolean(view && choice && (choice.send !== view.effective.send || choice.attach !== view.effective.attach));
  return {
    shown,
    choice,
    setChoice,
    /** Where they go (the extension's email address), when known. */
    address: view?.address ?? null,
    extension: view?.extension ?? null,
    /** Saves a change (with the rest of Your settings). */
    async save() {
      if (!domain || !view || !choice || !changed) return;
      setView(await recordsApi.saveVoicemailEmails(domain, { send: choice.send, attach: choice.attach }));
    }
  };
}

export function VoicemailEmailFields({ choices }: { choices: ReturnType<typeof useVoicemailEmailChoices> }) {
  const { shown, choice, setChoice, address, extension } = choices;
  if (!shown || !choice) return null;
  return (
    <fieldset className="plain-fieldset">
      <legend className="field-label">Voicemail emails</legend>
      <span className="hint">
        New voicemail in your mailbox{extension ? ` (${extension})` : ''}, emailed by Stitch to {address ? <strong>{address}</strong> : 'the email address on your extension'}. Anyone
        else signed in as your extension shares these choices.
      </span>
      <label className="check-row">
        <input type="checkbox" checked={choice.send} onChange={(e) => setChoice({ ...choice, send: e.target.checked })} />
        <span>Email my voicemail</span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={choice.attach} disabled={!choice.send} onChange={(e) => setChoice({ ...choice, attach: e.target.checked })} />
        <span>Attach the audio</span>
      </label>
    </fieldset>
  );
}
